/** Opportunity and risk feeds (spec §104, §105). */
import { useNavigate } from "react-router-dom";

import { api } from "@/api/client";
import { useInsights } from "@/hooks/useAnalytics";
import { inr } from "@/utils/format";

const PRIORITY_STYLE: Record<string, string> = {
  critical: "border-bad/40 bg-bad-soft text-bad",
  high: "border-warn/40 bg-warn-soft text-warn",
  medium: "border-rule-strong bg-surface-alt text-ink-soft",
  low: "border-rule bg-surface text-ink-faint",
};

export function InsightFeed({ kind }: { kind: "opportunity" | "risk" }) {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useInsights(kind);

  const dismiss = async (id: number) => {
    await api.post("/insights", { id, action: "dismiss" });
    refetch();
  };

  const open = (entityType: string, entityId: string) => {
    if (entityType === "client") navigate(`/clients/${entityId}`);
    if (entityType === "employee") navigate(`/employees/${entityId}`);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight capitalize">
          {kind === "opportunity" ? "Opportunities" : "Risks"}
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5">
          Ranked by priority then impact. Every card carries the metrics that
          triggered it — these are deterministic rules, not predictions.
        </p>
      </div>

      {isLoading && <div className="skeleton h-40" />}

      <div className="space-y-3">
        {(data?.items ?? []).map((insight) => (
          <article key={insight.id} className="card p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`chip ${PRIORITY_STYLE[insight.priority] ?? ""}`}>
                    {insight.priority}
                  </span>
                  <span className="label">{insight.rule_code}</span>
                </div>
                <h2 className="font-display font-semibold text-[16px] leading-snug">
                  {insight.title}
                </h2>
                <p className="text-[13.5px] text-ink-soft mt-1 leading-snug">{insight.body}</p>
                <p className="text-[13px] font-medium text-petrol mt-2">
                  → {insight.recommended_action}
                </p>
              </div>

              <div className="shrink-0 w-full sm:w-[280px]">
                <ul className="text-[12.5px] font-mono space-y-1 bg-surface-alt rounded-md p-2.5">
                  {insight.evidence.map((e) => (
                    <li key={e.label} className="flex justify-between gap-3">
                      <span className="text-ink-muted">{e.label}</span>
                      <span className="tnum">
                        {e.unit === "INR" ? inr(Number(e.value)) : String(e.value ?? "—")}
                        {e.unit && e.unit !== "INR" ? ` ${e.unit}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 mt-2">
                  <button className="btn btn-primary text-xs flex-1 justify-center"
                          onClick={() => open(insight.entity_type, insight.entity_id)}>
                    Open {insight.entity_type}
                  </button>
                  <button className="btn text-xs" onClick={() => dismiss(insight.id)}>
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))}
        {!isLoading && data?.items.length === 0 && (
          <div className="card p-8 text-center text-sm text-ink-muted">
            Nothing flagged for your scope right now.
          </div>
        )}
      </div>
    </div>
  );
}
